import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Section, Eyebrow, ProgressBar, NumberStat, Countdown } from "@/components/domain/Bits";
import RaffleCard from "@/components/domain/RaffleCard";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { money, num, raffleImg, onImgError, DELIVERY_STATUS, deliveryLabel } from "@/lib/format";
import {
  ShieldCheck, ArrowRight, Trophy, FileCheck2, Landmark, Radio, Users, CheckCircle2,
  Search, Ticket, Upload, ClipboardCheck, Eye, Sparkles, Vote,
} from "lucide-react";

const STEPS = [
  { icon: Ticket, t: "Elige tu sorteo", d: "Explora sorteos activos y sus premios." },
  { icon: Sparkles, t: "Selecciona boletos", d: "Manual, aleatorio o con la Maquinita de la Suerte." },
  { icon: FileCheck2, t: "Genera tu folio", d: "Recibe un folio único y seguro para tu reserva." },
  { icon: Landmark, t: "Transfiere", d: "Paga a una cuenta oficial verificada." },
  { icon: Upload, t: "Sube tu comprobante", d: "Adjunta tu comprobante de forma privada." },
  { icon: ClipboardCheck, t: "Validamos tu pago", d: "Nuestro equipo revisa y confirma tus boletos." },
];

export default function Home() {
  const [raffles, setRaffles] = useState([]);
  const [winners, setWinners] = useState([]);
  const [poll, setPoll] = useState(null);
  useEffect(() => {
    api.get("/raffles").then((r) => setRaffles(r.data));
    api.get("/winners").then((r) => setWinners(r.data));
    api.get("/polls/active").then((r) => setPoll(r.data)).catch(() => {});
  }, []);
  const featured = raffles.find((r) => r.featured) || raffles.find((r) => r.status === "active") || raffles[0];
  const { user } = useAuth();
  const showInventory = ["admin", "tenant_owner", "superadmin"].includes(user?.role);

  return (
    <div>
      {/* HERO */}
      <div className="relative overflow-hidden trust-gradient grid-noise text-white">
        <Section className="grid gap-12 py-16 lg:grid-cols-2 lg:py-24">
          <div className="flex flex-col justify-center">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-bright">
              <ShieldCheck className="h-3.5 w-3.5" />Operación verificable
            </span>
            <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              La confianza vende <span className="gold-text">antes que el premio.</span>
            </h1>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-slate-300">
              Sorteos documentados, ganadores verificables y pagos identificados con folio. Participa con la certeza de una plataforma tecnológica seria.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-brand text-white hover:bg-brand-hover" data-testid="hero-participate">
                <Link to="/sorteos">Ver sorteos activos <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="hero-transparency">
                <Link to="/transparencia"><ShieldCheck className="mr-2 h-4 w-4" />Centro de Transparencia</Link>
              </Button>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-6 border-t border-white/10 pt-8">
              {[["12,480", "Boletos vendidos"], ["38", "Sorteos realizados"], ["100%", "Premios entregados"]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-mono text-3xl font-bold tracking-tight sm:text-4xl">{v}</div>
                  <div className="mt-1 text-sm text-slate-400">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {featured && (
            <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}
              className="relative overflow-hidden rounded-2xl border border-white/10 bg-white shadow-2xl gold-ring">
              <div className="relative aspect-[16/11] overflow-hidden">
                <img src={raffleImg(featured)} onError={onImgError} alt={featured.name} className="h-full w-full object-cover" />
                <div className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1 text-xs font-bold text-white">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />Sorteo destacado
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold tracking-tight text-slate-900">{featured.name}</h3>
                <p className="mt-1 text-sm text-slate-500">{featured.prize}</p>
                <div className="mt-5 flex items-end justify-between">
                  <div><div className="text-xs text-slate-400">Boleto desde</div><div className="font-mono text-2xl font-bold text-slate-900">{money(featured.price)}</div></div>
                  <div className="text-right"><div className="text-xs text-slate-400">Vendido</div><div className="font-mono text-2xl font-bold text-brand">{featured.progress}%</div></div>
                </div>
                <div className="mt-3"><ProgressBar value={featured.progress} /></div>
                {showInventory && <div className="mt-2 text-xs text-slate-400" data-testid="featured-inventory"><span className="font-mono">{num(featured.available)}</span> boletos disponibles de <span className="font-mono">{num(featured.total_tickets)}</span></div>}
                <Button asChild className="mt-5 w-full rounded-full bg-brand hover:bg-brand-hover" data-testid="featured-cta">
                  <Link to={`/sorteos/${featured.id}`}>Participar en este sorteo</Link>
                </Button>
              </div>
            </motion.div>
          )}
        </Section>
      </div>

      {/* HOW TO */}
      <Section className="py-20">
        <div className="max-w-2xl">
          <Eyebrow>Cómo participar</Eyebrow>
          <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Un proceso claro, sin presión</h2>
          <p className="mt-3 text-slate-600">Primero entiendes cómo funciona. Luego participas con confianza.</p>
        </div>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-blue-50 text-brand"><s.icon className="h-5 w-5" /></div>
                <span className="font-mono text-sm font-bold text-slate-300">0{i + 1}</span>
              </div>
              <h3 className="mt-4 font-semibold text-slate-900">{s.t}</h3>
              <p className="mt-1 text-sm text-slate-500">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* ACTIVE RAFFLES */}
      <Section className="py-8">
        <div className="flex items-end justify-between">
          <div><Eyebrow>Sorteos</Eyebrow><h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Sorteos activos</h2></div>
          <Button asChild variant="ghost"><Link to="/sorteos">Ver todos <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {raffles.slice(0, 3).map((r, i) => <RaffleCard key={r.id} raffle={r} index={i} />)}
        </div>
      </Section>

      {/* TRUST STRIP */}
      <Section className="py-20">
        <div className="grid gap-6 rounded-2xl trust-gradient p-10 text-white lg:grid-cols-4">
          {[[ShieldCheck, "Cuentas oficiales", "Solo mostramos cuentas bancarias activas y verificadas."],
            [Radio, "Resultados públicos", "Cada sorteo se transmite y su resultado queda documentado."],
            [Trophy, "Ganadores verificables", "Perfiles públicos con evidencia de entrega."],
            [Eye, "Trazabilidad total", "Consulta tu operación en cualquier momento por folio."]].map(([Icon, t, d], i) => (
            <div key={i}>
              <Icon className="h-6 w-6 text-brand-bright" />
              <h3 className="mt-3 font-semibold">{t}</h3>
              <p className="mt-1 text-sm text-slate-300">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* POLL */}
      {poll && (
        <Section className="py-8">
          <div className="relative overflow-hidden rounded-3xl bg-slate-900 px-8 py-10 text-white sm:px-12" data-testid="home-poll-banner">
            <div className="relative z-10 grid gap-8 lg:grid-cols-[1.3fr_1fr] lg:items-center">
              <div>
                <Eyebrow><Vote className="h-3.5 w-3.5" />Tu opinión decide</Eyebrow>
                <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">{poll.question}</h2>
                <p className="mt-2 text-slate-300">{poll.total_votes} personas ya votaron. Elige el premio que quieres ver en el próximo sorteo.</p>
                <Button asChild className="mt-6 rounded-full bg-brand hover:bg-brand-hover" data-testid="home-poll-cta"><Link to="/encuesta">Votar ahora <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
              </div>
              <ul className="space-y-2">
                {poll.options.slice(0, 4).map((o) => (
                  <li key={o.id} className="flex items-center justify-between rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm backdrop-blur"><span className="font-medium">{o.text}</span>{poll.show_results && <span className="font-mono text-slate-300">{o.pct}%</span>}</li>
                ))}
              </ul>
            </div>
          </div>
        </Section>
      )}

      {/* WINNERS */}
      <Section className="py-8">
        <div className="flex items-end justify-between">
          <div><Eyebrow>Casos reales</Eyebrow><h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">Ganadores recientes</h2></div>
          <Button asChild variant="ghost"><Link to="/ganadores">Ver todos <ArrowRight className="ml-1 h-4 w-4" /></Link></Button>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {winners.slice(0, 3).map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}>
              <Link to={`/ganadores/${w.id}`} className="group block overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-xl" data-testid={`home-winner-${w.id}`}>
                <div className="aspect-[4/3] overflow-hidden"><img src={w.gallery?.[0]} alt={w.name_public} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
                <div className="p-5">
                  <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${DELIVERY_STATUS[w.delivery_status] || DELIVERY_STATUS.Pendiente}`}><CheckCircle2 className="h-3 w-3" />{deliveryLabel(w)}</div>
                  <h3 className="mt-3 font-bold text-slate-900">{w.name_public} · {w.city}</h3>
                  <p className="mt-1 text-sm text-slate-500">Ganó {w.prize}</p>
                  <div className="mt-3 font-mono text-sm font-semibold text-brand">Boleto #{w.winning_number}</div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-20">
        <div className="relative overflow-hidden rounded-3xl trust-gradient p-10 text-center shadow-xl gold-ring sm:p-16">
          <div className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #E3C05A 0%, transparent 70%)" }} />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-72 w-72 rounded-full opacity-10" style={{ background: "radial-gradient(circle, #B4892B 0%, transparent 70%)" }} />
          <span className="relative inline-flex items-center gap-2 rounded-full border border-brand/40 bg-brand/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-bright"><Sparkles className="h-3.5 w-3.5" />Círculo Dorado</span>
          <h2 className="relative mx-auto mt-5 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">¿Listo para participar con <span className="gold-text">confianza</span>?</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-slate-300">Explora nuestros sorteos activos o consulta el estado de tu participación por folio.</p>
          <div className="relative mt-8 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg" className="rounded-full bg-brand text-white hover:bg-brand-hover"><Link to="/sorteos">Ver sorteos</Link></Button>
            <Button asChild size="lg" variant="outline" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white"><Link to="/consulta"><Search className="mr-2 h-4 w-4" />Consultar folio</Link></Button>
          </div>
        </div>
      </Section>
    </div>
  );
}
